import { useQuery } from '@tanstack/react-query';

import { getAppContext } from './app-context';
import { axiosForBackend } from './http';

/** 登录态与角色。角色只有 admin 和 user 两种 */

export interface CurrentUser {
  user_id: string;
  name: string;
  email?: string;
  avatar?: string;
  lark_user_id?: string;
  role: string;
}

interface MeResponse {
  authenticated: boolean;
  user: CurrentUser | null;
}

/** 首屏从服务端注入的上下文取，避免白屏 */
function initialUser(): CurrentUser | undefined {
  const context = getAppContext();
  if (!context.authenticated) {
    return undefined;
  }
  return {
    user_id: context.userId,
    name: context.userName,
    email: context.email,
    avatar: context.avatar,
    role: context.roles[0] ?? 'user',
  };
}

async function fetchMe(): Promise<CurrentUser | null> {
  try {
    const { data } = await axiosForBackend.get<MeResponse>('/api/auth/me');
    return data.authenticated ? data.user : null;
  } catch {
    return null;
  }
}

export interface AuthState {
  /** null 表示未登录；加载中时也是 null，配合 isLoading 区分 */
  user: CurrentUser | null;
  isAdmin: boolean;
  isLoading: boolean;
}

/**
 * 当前登录人。
 *
 * 全应用共享一份缓存，只发一次 /api/auth/me。服务端注入的登录态只用来
 * 首屏占位（initialDataUpdatedAt 置 0 让它立刻过期），挂载后仍以接口为准。
 */
export function useAuth(): AuthState {
  const { data } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    initialData: initialUser,
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const user: CurrentUser | null = data ?? null;
  return {
    user,
    isAdmin: user?.role === 'admin',
    isLoading: data === undefined,
  };
}

/** 退出登录；失败时抛错，由调用方提示 */
export async function signOut(): Promise<void> {
  await axiosForBackend.post('/api/auth/logout');
}
